
import { Progress } from "@/components/ui/progress";
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

interface AnalysisProgressProps {
  progress: number;
  isAnalyzing: boolean;
  status?: 'idle' | 'processing' | 'completed' | 'error';
}

const AnalysisProgress = ({ progress, isAnalyzing, status = 'processing' }: AnalysisProgressProps) => {
  const getStatusText = () => {
    switch (status) {
      case 'processing':
        if (progress < 25) return "Starting analysis...";
        if (progress < 50) return "Processing content...";
        if (progress < 75) return "Analyzing patterns...";
        if (progress < 95) return "Finalizing results...";
        return "Completing analysis...";
      case 'completed':
        return "Analysis complete!";
      case 'error':
        return "Analysis failed. Please try again.";
      default:
        return "Ready to analyze";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Use a pulsing animation when progress is high but not complete
  const isPulsing = status === 'processing' && progress > 80;

  return (
    <div className="space-y-4">
      <Progress 
        value={progress} 
        className={`h-2 ${status === 'error' ? 'bg-red-200' : ''} ${isPulsing ? 'animate-pulse' : ''}`}
      />
      <div className="flex items-center justify-center gap-2 text-sm text-center text-muted-foreground">
        {getStatusIcon()}
        <p>{getStatusText()}</p>
        {isPulsing && <span className="text-xs text-fraudeye-accent">Almost done...</span>}
      </div>
    </div>
  );
};

export default AnalysisProgress;
